import api from './api';
import userService from './userService';

/**
 * The reads and writes behind the admin area.
 *
 * Every route here is admin-only on the server, so a member who reaches one
 * of these calls gets a refusal rather than a partial answer; the tabs report
 * that refusal rather than rendering an empty table.
 *
 * The three member-directory calls delegate to `userService` rather than
 * building their own requests. Two axios callers for one route drift apart,
 * and the admin area is not the place that gets to own the member routes.
 */
// The name to save the export under when the response carries no
// Content-Disposition, which is what a deployment that does not expose the
// header to the browser looks like. A file named after the route would be
// worse than a plain one.
const EXPORT_FALLBACK_FILENAME = 'furniture-exchange-report.csv';

function filenameFrom(disposition) {
    const match = /filename="([^"]+)"/.exec(disposition || '');
    return match ? match[1] : EXPORT_FALLBACK_FILENAME;
}

function rangeParams({ from, to } = {}) {
    return { ...(from && { from }), ...(to && { to }) };
}

export const adminService = {
    listUsers(params = {}) {
        return userService.getAllUsers(params);
    },

    setUserActive(id, isActive) {
        return userService.updateUser(id, { isActive });
    },

    /**
     * Replace a member's roles.
     *
     * The route takes the whole array and refuses an empty one, so a caller
     * removing the last role has to send what remains rather than nothing.
     */
    setUserRoles(id, roles) {
        return userService.updateUserRoles(id, roles);
    },

    /**
     * The aggregate summary. `range` is `{ from, to }` as YYYY-MM-DD days,
     * either optional; a blank value is left off the query so the server
     * reads it as unbounded.
     */
    async reportSummary(range = {}) {
        const response = await api.get('/reports/summary', {
            params: rangeParams(range),
        });
        return response.data;
    },

    /**
     * The completed exchanges as a CSV file.
     *
     * The route is admin-authenticated, so the file has to arrive through
     * this axios instance, which is what attaches the bearer token; a plain
     * navigation to the same address carries no credential and answers 401.
     * The caller gets the body and the name to save it under rather than a
     * URL, because turning the body into a download is the page's job.
     */
    async exportCsv() {
        const response = await api.get('/reports/export.csv', {
            responseType: 'blob',
        });
        return {
            blob: response.data,
            filename: filenameFrom(response.headers?.['content-disposition']),
        };
    },

    /** The month-by-month impact file for the same range the page shows. */
    async impactCsv(range = {}) {
        const response = await api.get('/reports/impact.csv', {
            params: rangeParams(range),
            responseType: 'blob',
        });
        return {
            blob: response.data,
            filename: filenameFrom(response.headers?.['content-disposition']),
        };
    },
};

export default adminService;
